import React from 'react';

// Inline styles to make this component self-contained
const styles = `
/* Logo Component */
.logo-link {
  text-decoration: none;
}

.logo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.logo-text {
  font-family: 'Space Grotesk', sans-serif;
  color: #ffffff;
  font-weight: 400;
  letter-spacing: 0.05em;
  line-height: 1;
  transition: all 0.3s ease;
}

.logo-tagline {
  font-family: 'Space Grotesk', sans-serif;
  color: #f6a200;
  font-weight: 400;
  letter-spacing: 0.15em;
  line-height: 1;
  transition: all 0.3s ease;
}

/* Size Variants */
.logo-small .logo-text {
  font-size: 1.25rem;
}

.logo-small .logo-tagline {
  font-size: 0.625rem;
}

.logo-medium .logo-text {
  font-size: 1.75rem;
}

.logo-medium .logo-tagline {
  font-size: 0.65rem;
}

.logo-large .logo-text {
  font-size: 2.5rem;
}

.logo-large .logo-tagline {
  font-size: 0.875rem;
}

.logo-xlarge .logo-text {
  font-size: 3.5rem;
}

.logo-xlarge .logo-tagline {
  font-size: 1.1rem;
}

/* Animation Variants */
.logo-animated .logo-text {
  background: linear-gradient(
    90deg,
    #ffffff 0%,
    #ffffff 40%,
    #ffb733 50%,
    #ffffff 60%,
    #ffffff 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: logoShimmer 3s ease-in-out infinite;
}

.logo-animated:hover .logo-text {
  animation: logoShimmer 1.5s ease-in-out infinite;
}

.logo-animated .logo-tagline {
  position: relative;
}

.logo-animated:hover .logo-tagline {
  text-shadow: 0 0 20px rgba(246, 162, 0, 0.5);
}

/* Glow effect on hover */
.logo-link:hover .logo-animated .logo-text,
.logo-animated:hover .logo-text {
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
}

.logo-link:hover .logo-animated .logo-tagline,
.logo-animated:hover .logo-tagline {
  filter: drop-shadow(0 0 10px rgba(246, 162, 0, 0.4));
}

/* Shimmer Animation */
@keyframes logoShimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: -100% 50%;
  }
}

/* Center aligned variant */
.logo.logo-center {
  align-items: center;
  text-align: center;
}

/* Responsive */
@media (max-width: 768px) {
  .logo-medium .logo-text {
    font-size: 1.5rem;
  }

  .logo-medium .logo-tagline {
    font-size: 0.55rem;
  }

  .logo-large .logo-text {
    font-size: 2rem;
  }

  .logo-large .logo-tagline {
    font-size: 0.75rem;
  }
}
`;

const Logo = ({
  size = 'medium',
  animated = true,
  onClick = null,
  showTagline = true,
  center = false,
  className = ''
}) => {
  const logoContent = (
    <div
      className={`logo logo-${size} ${animated ? 'logo-animated' : ''} ${center ? 'logo-center' : ''} ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      <span className="logo-text">DUTCHCRAFT</span>
      {showTagline && <span className="logo-tagline">BOARD</span>}
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      {logoContent}
    </>
  );
};

export default Logo;

/*
Usage:
  import Logo from './Logo';

  <Logo />                                    // Default: medium, animated
  <Logo size="large" />                       // Sizes: small, medium, large, xlarge
  <Logo animated={false} />                   // Disable shimmer animation
  <Logo onClick={() => navigate('/')} />      // Add click handler
  <Logo showTagline={false} />                // Hide "BOARD" tagline
  <Logo center />                             // Center aligned

Note: Requires 'Space Grotesk' font. Add to your HTML:
  <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap" rel="stylesheet">
*/
